import { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Search, Plus, MapPin, Shield, Minus, Plus as PlusIcon, LocateFixed, LogOut, User, Coins, Leaf, Map as MapIcon, Music2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import { fetchEvents } from "@/lib/mapioApi";
import { CATEGORIES, DEFAULT_CENTER, DEFAULT_ZOOM, FALLBACK_LOGO, THEMES } from "@/lib/mapioConstants";
import EventDetailPanel from "@/components/EventDetailPanel";
import AddEventModal from "@/components/AddEventModal";
import CategoryChips from "@/components/CategoryChips";
import AuthModal from "@/components/AuthModal";
import EditProfileModal from "@/components/EditProfileModal";
import WalletModal from "@/components/WalletModal";
import { useAuth } from "@/contexts/AuthContext";

const THEME_ICONS = {
  Map: MapIcon,
  Leaf,
  Music2,
};

const THEME_ROUTES = {
  simple: "/",
  green: "/green",
  disco: "/disco",
};

const ADMIN_EMAIL = "xdropxhunter@gmail.com";

const OVERVIEW_ZOOM = 11;

const CLUSTER_PIXEL_DISTANCE = 86;

const getSearchShakeLevel = (value) => {
  const cleaned = value.trim();
  if (!/^(67)+$/.test(cleaned)) return "";
  const repeatCount = Math.min(cleaned.length / 2, 3);
  return ["", "normal", "medium", "blast"][repeatCount] || "blast";
};

const buildClusterIcon = (count, themeId = "simple", hasDisco = false) =>
  L.divIcon({
    className: `mapio-cluster mapio-cluster-${themeId} ${hasDisco ? "mapio-cluster-disco" : ""}`,
    html: `
      <div class="mapio-cluster-core">
        <span>${count}</span>
        <small>${hasDisco ? "clubs" : "events"}</small>
      </div>
    `,
    iconSize: [68, 68],
    iconAnchor: [34, 34],
  });

const buildPinIcon = (event, themeId = "simple", zoom = DEFAULT_ZOOM) => {
  const isOverview = zoom <= OVERVIEW_ZOOM;

  if (event?.category === "Disco") {
    const size = isOverview ? 42 : 78;
    const imageSize = isOverview ? 40 : 72;
    return L.divIcon({
      className: `mapio-pin mapio-club-asset-marker ${isOverview ? "mapio-club-asset-marker-compact" : ""}`,
      html: `
        <div class="club-asset-pin" style="width:${size}px;height:${size}px">
          <img src="/assets/disco-club-marker.png" alt="" style="width:${imageSize}px;height:${imageSize}px" />
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, Math.round(size * 0.58)],
    });
  }

  const size = isOverview ? 34 : 48;

  return L.divIcon({
    className: `mapio-pin mapio-pin-${themeId} ${isOverview ? "mapio-pin-compact" : ""}`,
    html: `
      <div class="pin-wrapper" style="width:${size}px;height:${size}px">
        <img src="${event?.logo_image || FALLBACK_LOGO}" onerror="this.src='${FALLBACK_LOGO}'" alt="" />
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const userLocationIcon = L.divIcon({
  className: "user-location-marker",
  html: `
    <div class="user-loc-container">
      <div class="user-loc-pulse"></div>
      <div class="user-loc-pulse-delayed"></div>
      <div class="user-loc-ring"></div>
      <div class="user-loc-dot"></div>
    </div>
  `,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

const ZoomControls = ({ onLocate, locating }) => {
  const map = useMap();
  return (
    <div className="map-zoom-controls absolute right-4 z-[450] flex flex-col gap-2">
      <button
        data-testid="locate-me-btn"
        onClick={onLocate}
        className={`w-11 h-11 rounded-full glass flex items-center justify-center transition no-tap-highlight ${
          locating ? "text-blue-600 animate-pulse" : "text-gray-800 hover:bg-white"
        }`}
        aria-label="Locate me"
      >
        <LocateFixed className="w-5 h-5" />
      </button>
      <button
        data-testid="map-zoom-in-btn"
        onClick={() => map.setZoom(map.getZoom() + 1)}
        className="w-11 h-11 rounded-full glass flex items-center justify-center text-gray-800 hover:bg-white transition no-tap-highlight"
        aria-label="Zoom in"
      >
        <PlusIcon className="w-5 h-5" />
      </button>
      <button
        data-testid="map-zoom-out-btn"
        onClick={() => map.setZoom(map.getZoom() - 1)}
        className="w-11 h-11 rounded-full glass flex items-center justify-center text-gray-800 hover:bg-white transition no-tap-highlight"
        aria-label="Zoom out"
      >
        <Minus className="w-5 h-5" />
      </button>
    </div>
  );
};

const FlyToLocation = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1 });
    }
  }, [position, map]);
  return null;
};

const FlyToEvent = ({ event }) => {
  const map = useMap();
  useEffect(() => {
    if (event) {
      const timer = window.setTimeout(() => {
        map.flyTo([event.lat, event.lng], 14, {
          duration: 0.45,
          easeLinearity: 0.25,
        });
      }, 80);
      return () => window.clearTimeout(timer);
    }
  }, [event, map]);
  return null;
};

const ZoomWatcher = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });

  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
};

const EventMapMarker = ({ marker, themeId, zoom, onSelect }) => {
  const map = useMap();

  return (
    <Marker
      position={marker.position}
      icon={
        marker.type === "cluster"
          ? buildClusterIcon(marker.count, themeId, marker.hasDisco)
          : buildPinIcon(marker.event, themeId, zoom)
      }
      eventHandlers={{
        click: () => {
          if (marker.type === "cluster") {
            map.flyTo(marker.position, Math.min(map.getZoom() + 2, 13), {
              duration: 0.35,
              easeLinearity: 0.3,
            });
            return;
          }
          onSelect(marker.event);
        },
      }}
    />
  );
};

const distanceBetweenPoints = (a, b) => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
};

const mergeCloseClusterGroups = (groups) => {
  let merged = true;

  while (merged) {
    merged = false;
    for (let i = 0; i < groups.length; i += 1) {
      for (let j = i + 1; j < groups.length; j += 1) {
        if (distanceBetweenPoints(groups[i].point, groups[j].point) > CLUSTER_PIXEL_DISTANCE) {
          continue;
        }

        const first = groups[i];
        const second = groups[j];
        const count = first.events.length + second.events.length;

        first.events = [...first.events, ...second.events];
        first.latTotal += second.latTotal;
        first.lngTotal += second.lngTotal;
        first.point = {
          x: (first.point.x * (count - second.events.length) + second.point.x * second.events.length) / count,
          y: (first.point.y * (count - second.events.length) + second.point.y * second.events.length) / count,
        };
        first.hasDisco = first.hasDisco || second.hasDisco;
        groups.splice(j, 1);
        merged = true;
        break;
      }
      if (merged) break;
    }
  }

  return groups;
};

const buildVisibleMarkers = (events, map, zoom, search) => {
  if (zoom > OVERVIEW_ZOOM || events.length <= 1 || search.trim()) {
    return events.map((event) => ({
      id: event.id,
      type: "event",
      event,
      position: [event.lat, event.lng],
    }));
  }

  const groups = [];

  events.forEach((event) => {
    const point = map.latLngToLayerPoint([event.lat, event.lng]);
    const existing = groups.find((group) => distanceBetweenPoints(group.point, point) <= CLUSTER_PIXEL_DISTANCE);

    if (existing) {
      const previousCount = existing.events.length;
      const nextCount = previousCount + 1;
      existing.events.push(event);
      existing.latTotal += event.lat;
      existing.lngTotal += event.lng;
      existing.point = {
        x: (existing.point.x * previousCount + point.x) / nextCount,
        y: (existing.point.y * previousCount + point.y) / nextCount,
      };
      existing.hasDisco = existing.hasDisco || event.category === "Disco";
      return;
    }

    groups.push({
      events: [event],
      latTotal: event.lat,
      lngTotal: event.lng,
      point,
      hasDisco: event.category === "Disco",
    });
  });

  return mergeCloseClusterGroups(groups).map((group, index) => {
    if (group.events.length === 1) {
      const [event] = group.events;
      return {
        id: event.id,
        type: "event",
        event,
        position: [event.lat, event.lng],
      };
    }

    return {
      id: `cluster-${zoom}-${index}-${group.events.map((event) => event.id).join("-")}`,
      type: "cluster",
      events: group.events,
      count: group.events.length,
      hasDisco: group.hasDisco,
      position: [
        group.latTotal / group.events.length,
        group.lngTotal / group.events.length,
      ],
    };
  });
};

const EventMarkerLayer = ({ events, themeId, zoom, search, onSelect }) => {
  const map = useMap();
  const [mapRevision, setMapRevision] = useState(0);

  useMapEvents({
    moveend: () => setMapRevision((revision) => revision + 1),
    resize: () => setMapRevision((revision) => revision + 1),
    zoomend: () => setMapRevision((revision) => revision + 1),
  });

  const visibleMarkers = useMemo(
    () => {
      void mapRevision;
      return buildVisibleMarkers(events, map, zoom, search);
    },
    [events, map, mapRevision, search, zoom]
  );

  return visibleMarkers.map((marker) => (
    <EventMapMarker
      key={marker.id}
      marker={marker}
      themeId={themeId}
      zoom={zoom}
      onSelect={onSelect}
    />
  ));
};

const ThemeSwitcher = ({ active, activeTheme }) => {
  const ActiveIcon = THEME_ICONS[activeTheme.icon] || MapIcon;

  return (
    <div className="theme-dock absolute left-1/2 -translate-x-1/2 z-[450]">
      <div className="theme-dock-shell">
        <div className="theme-dock-copy hidden sm:flex">
          <ActiveIcon className="w-4 h-4" />
          <div>
            <p>{activeTheme.label} map</p>
            <span>{activeTheme.tagline}</span>
          </div>
          <div className="disco-eq" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </div>
        </div>
        <div className="theme-switcher" role="group" aria-label="Map theme">
          {THEMES.map((t) => {
            const Icon = THEME_ICONS[t.icon] || MapIcon;
            const isActive = active === t.id;
            return (
              <NavLink
                key={t.id}
                data-testid={`theme-${t.id}-btn`}
                to={THEME_ROUTES[t.id] || "/"}
                className={`theme-switcher-btn ${isActive ? "theme-switcher-btn-active" : ""}`}
                title={`${t.label} map theme`}
                aria-pressed={isActive}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function MapHome({ initialTheme = "simple", initialCategory = "All" }) {
  const { user, profile, signOut } = useAuth();
  const avatarUrl = profile?.avatar_url || user?.avatar_url;
  const displayName = profile?.username || user?.username || user?.email || "User";
  const userEmail = (profile?.email || user?.email || "").toLowerCase();
  const isAdmin = userEmail === ADMIN_EMAIL;
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [flyToUser, setFlyToUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [shakeLevel, setShakeLevel] = useState("");
  const [activeThemeId, setActiveThemeId] = useState(initialTheme);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const userMenuRef = useRef(null);
  const activeTheme = useMemo(
    () => THEMES.find((theme) => theme.id === activeThemeId) || THEMES[0],
    [activeThemeId]
  );
  const isDiscoTheme = activeThemeId === "disco";
  const isDiscoCategory = activeCategory === "Disco";

  useEffect(() => {
    setActiveThemeId(initialTheme);
    setActiveCategory(initialCategory);
    setSearch("");
    setSelected(null);
  }, [initialCategory, initialTheme]);

  // Auto-close auth modal when user logs in (e.g. after Google OAuth redirect)
  useEffect(() => {
    if (user) {
      setShowAuth(false);
      setShowUserMenu(false);
    }
  }, [user]);

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(loc);
        setFlyToUser(loc);
        setLocating(false);
      },
      (err) => {
        console.error("Location error:", err);
        alert("Could not get your location. Please allow location access.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const loadEvents = async (category) => {
    setLoading(true);
    try {
      const data = await fetchEvents(category);
      setEvents(data);
    } catch (e) {
      console.error("Failed to load events", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    let alive = true;
    fetchEvents("All")
      .then((data) => {
        if (alive) setAllEvents(data);
      })
      .catch((e) => console.error("Failed to load searchable events", e));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (isDiscoCategory && activeThemeId !== "disco") {
      setActiveThemeId("disco");
    }
  }, [isDiscoCategory, activeThemeId]);

  const categoryScopedEvents = useMemo(() => {
    if (activeCategory === "Disco") return events;
    return events.filter((event) => event.category !== "Disco");
  }, [events, activeCategory]);

  const searchableEvents = useMemo(() => {
    const source = allEvents.length ? allEvents : events;
    if (activeCategory === "Disco") {
      return source.filter((event) => event.category === "Disco");
    }
    if (activeCategory !== "All") {
      return source.filter((event) => event.category === activeCategory);
    }
    return source.filter((event) => event.category !== "Disco");
  }, [activeCategory, allEvents, events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categoryScopedEvents;
    return categoryScopedEvents.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.location_area.toLowerCase().includes(q) ||
        e.host_name.toLowerCase().includes(q)
    );
  }, [categoryScopedEvents, search]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return searchableEvents
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.location_area.toLowerCase().includes(q) ||
          e.host_name.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [searchableEvents, search]);

  const handleSelectSearchResult = (event) => {
    setSelected(event);
    setSearch(event.name);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    const nextShakeLevel = getSearchShakeLevel(value);
    if (nextShakeLevel) {
      setShakeLevel("");
      window.requestAnimationFrame(() => {
        setShakeLevel(nextShakeLevel);
      });
    }
  };

  return (
    <div
      className={`map-home-theme map-home-theme-${activeThemeId} ${shakeLevel ? `map-home-shake map-home-shake-${shakeLevel}` : ""} relative w-screen h-screen overflow-hidden`}
      onAnimationEnd={() => setShakeLevel("")}
      style={{
        "--theme-primary": activeTheme.primary,
        "--theme-secondary": activeTheme.secondary,
        "--theme-surface": activeTheme.surface,
        "--theme-text": activeTheme.text,
      }}
      data-testid="map-home"
    >
      {/* Map */}
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        zoomControl={false}
        style={{ width: "100%", height: "100%" }}
        attributionControl={true}
      >
        <TileLayer
          key={activeTheme.id}
          url={activeTheme.tiles}
          attribution={activeTheme.attribution}
        />
        <ZoomWatcher onZoomChange={setMapZoom} />
        <EventMarkerLayer
          events={filtered}
          themeId={activeThemeId}
          zoom={mapZoom}
          search={search}
          onSelect={setSelected}
        />
        {userLocation && (
          <Marker
            position={userLocation}
            icon={userLocationIcon}
            interactive={false}
          />
        )}
        <ZoomControls onLocate={handleLocateMe} locating={locating} />
        <FlyToEvent event={selected} />
        <FlyToLocation position={flyToUser} />
      </MapContainer>
      {activeThemeId !== "simple" && <div className="theme-atmosphere" aria-hidden="true" />}
      {isDiscoTheme && (
        <div className="disco-motion-layer" aria-hidden="true">
          <span className="disco-beam disco-beam-1" />
          <span className="disco-beam disco-beam-2" />
          <span className="disco-beam disco-beam-3" />
          <span className="disco-spark disco-spark-1" />
          <span className="disco-spark disco-spark-2" />
          <span className="disco-spark disco-spark-3" />
          <span className="disco-spark disco-spark-4" />
          <span className="disco-spark disco-spark-5" />
          <span className="disco-spark disco-spark-6" />
        </div>
      )}

      {/* Top floating navbar */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] w-[95%] max-w-3xl">
        <div className="glass rounded-full flex items-center gap-2 px-2 py-2">
          <div className="flex items-center gap-2 pl-3 pr-1 select-none">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-colors"
              style={{ background: `linear-gradient(135deg, ${activeTheme.primary}, ${activeTheme.secondary})` }}
            >
              <MapPin className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span
              data-testid="brand-logo"
              className="theme-brand-text font-extrabold text-lg tracking-tight text-gray-900 hidden sm:block"
              style={{ fontFamily: "Manrope" }}
            >
              Mapix
            </span>
          </div>

          <div className="theme-search-wrap relative flex-1">
            <div className="theme-search-box flex items-center gap-2 bg-white/60 rounded-full px-4 py-2 border border-white/60">
            <Search className="w-4 h-4 text-gray-500 shrink-0" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search events, areas, hosts…"
              className="theme-search-input bg-transparent outline-none text-sm w-full text-gray-900 placeholder:text-gray-500"
            />
            {search.trim() && (
              <button
                type="button"
                data-testid="clear-search-btn"
                onClick={() => setSearch("")}
                className="search-clear-btn"
                aria-label="Clear search"
              >
                x
              </button>
            )}
            </div>

            <AnimatePresence>
              {search.trim() && (
                <motion.div
                  data-testid="search-results"
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.14 }}
                  className="search-results-panel"
                >
                  {searchResults.length > 0 ? (
                    searchResults.map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        data-testid={`search-result-${event.id}`}
                        onClick={() => handleSelectSearchResult(event)}
                        className="search-result-row"
                      >
                        <img
                          src={event.logo_image || FALLBACK_LOGO}
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_LOGO;
                          }}
                          alt=""
                        />
                        <span>
                          <strong>{event.name}</strong>
                          <small>{event.location_area} - {event.category}</small>
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="search-empty-state">No matching events found</div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            data-testid="add-event-btn"
            onClick={() => setShowAdd(true)}
            className="theme-primary-btn shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Event</span>
          </button>

          {user && (
            <button
              type="button"
              onClick={() => setShowWallet(true)}
              className="shrink-0 hidden sm:flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-white/70 border border-white/70 text-gray-800 text-sm font-bold shadow-sm hover:bg-white transition"
            >
              <Coins className="w-4 h-4 text-blue-600" />
              {profile?.mo_balance ?? user?.mo_balance ?? 0} MO
            </button>
          )}

          {/* User Avatar / Login */}
          <div className="relative" ref={userMenuRef}>
            {user ? (
              <button
                data-testid="user-avatar-btn"
                onClick={() => setShowUserMenu((s) => !s)}
                className="w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </button>
            ) : (
              <button
                data-testid="login-btn"
                onClick={() => setShowAuth(true)}
                className="w-10 h-10 rounded-full bg-white/70 border border-gray-200 flex items-center justify-center hover:bg-white transition shadow-sm"
              >
                <User className="w-4 h-4 text-gray-600" />
              </button>
            )}

            {/* Dropdown */}
            <AnimatePresence>
              {showUserMenu && user && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.95 }}
                  className="absolute right-0 top-12 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[600]"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 truncate" style={{ fontFamily: "Manrope" }}>
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowWallet(true);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition border-b border-gray-100"
                  >
                    <Coins className="w-4 h-4" />
                    MO Wallet
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowEditProfile(true);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition border-b border-gray-100"
                  >
                    <User className="w-4 h-4" />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => {
                      signOut();
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <CategoryChips
        active={activeCategory}
        onChange={setActiveCategory}
        categories={CATEGORIES}
        theme={activeThemeId}
      />

      <ThemeSwitcher
        active={activeThemeId}
        activeTheme={activeTheme}
      />

      {/* Admin link (subtle) */}
      {isAdmin && (
        <Link
          to="/admin"
          data-testid="admin-link"
          className="admin-link absolute left-4 z-[450] glass rounded-full px-3 py-2 text-xs font-medium text-gray-700 hover:text-blue-600 transition flex items-center gap-1.5"
        >
          <Shield className="w-3.5 h-3.5" />
          Admin
        </Link>
      )}

      {/* Loading shimmer hint */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="loading-events-pill absolute left-1/2 -translate-x-1/2 z-[400] glass rounded-full px-4 py-1.5 text-xs text-gray-700"
          >
            Loading events…
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail panel */}
      <EventDetailPanel
        event={selected}
        onClose={() => setSelected(null)}
      />

      {/* Add Event modal */}
      <AddEventModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => {
          setShowAdd(false);
        }}
      />

      {/* Empty state hint */}
      {!loading && filtered.length === 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[400] glass rounded-2xl px-5 py-3 text-sm text-gray-700">
          No events found. Try a different category or clear search.
        </div>
      )}

      {/* Standalone Auth Modal (triggered from navbar login btn) */}
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onAuth={() => setShowAuth(false)}
      />

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={showEditProfile}
        onClose={() => setShowEditProfile(false)}
      />

      <WalletModal
        open={showWallet}
        onClose={() => setShowWallet(false)}
      />
    </div>
  );
}
